import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { statusOptions, statusDisplayMap } from "@/constants/proposalMeta";
import { useAuth } from "@/components/AuthProvider";
import { MultiSelectProducts } from "@/components/invoices/MultiSelectProducts";

// Form schema for proposal validation
const proposalSchema = z.object({
  title: z.string().min(1, "Title is required"),
  client_id: z.string().min(1, "Client is required"),
  value: z.coerce.number().min(0, "Initial estimated value must be positive"),
  final_value: z.coerce.number().min(0, "Final value must be positive").optional(),
  probability: z.coerce.number().min(0).max(100).optional(),
  status: z.enum(["draft", "pending", "accepted", "rejected"]),
  sent_date: z.date().optional(),
  response_deadline: z.date().optional(),
  expected_invoicing_date: z.date().optional(),
  tags: z.array(z.string()).optional(),
  description: z.string().optional(),
  tools: z.array(z.string()).optional(),
  led_by: z.enum(["initiative", "custom_activity", "customer_request"]).optional(),
  initiative_id: z.string().optional(),
});

type ProposalFormData = z.infer<typeof proposalSchema>;

interface ProposalFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposal?: any;
  clients: Array<{ id: string; name: string }>;
}

export function ProposalForm({ open, onOpenChange, proposal, clients }: ProposalFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [attachment, setAttachment] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  // Generate a local preview URL for the selected file
  useEffect(() => {
    if (!attachment) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachment);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  // Fetch available tools/products
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, category")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch initiatives for led_by dropdown
  const { data: initiatives = [] } = useQuery({
    queryKey: ["initiatives"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("initiatives")
        .select("id, name")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch existing attachments for the proposal (when editing)
  const { data: proposalDocuments = [] } = useQuery({
    queryKey: ["proposal-documents", proposal?.id],
    queryFn: async () => {
      if (!proposal?.id) return [];
      
      const { data, error } = await supabase
        .from("proposal_documents")
        .select("*")
        .eq("proposal_id", proposal.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!proposal?.id
  });

  // Form setup
  const form = useForm({
    resolver: zodResolver(proposalSchema),
    defaultValues: {
      title: "",
      client_id: "",
      value: 0,
      final_value: undefined,
      probability: undefined,
      status: "pending",
      sent_date: undefined,
      response_deadline: undefined,
      expected_invoicing_date: undefined,
      tags: [],
      description: "",
      tools: [],
      led_by: undefined,
      initiative_id: undefined,
    },
  });

  // Reset form when proposal changes (for editing)
  useEffect(() => {
    if (proposal) {
      form.reset({
        title: proposal.title || "",
        client_id: proposal.client_id || "",
        value: proposal.value || 0,
        final_value: proposal.final_value || undefined,
        probability: proposal.probability ?? undefined,
        status: proposal.status || "pending",
        sent_date: proposal.sent_date ? new Date(proposal.sent_date) : undefined,
        response_deadline: proposal.response_deadline ? new Date(proposal.response_deadline) : undefined,
        expected_invoicing_date: proposal.expected_invoicing_date ? new Date(proposal.expected_invoicing_date) : undefined,
        tags: proposal.tags || [],
        description: proposal.description || "",
        tools: proposal.tools || [],
        led_by: proposal.led_by || undefined,
        initiative_id: proposal.initiative_id || undefined,
      });
    } else {
      form.reset({
        title: "",
        client_id: "",
        value: 0,
        final_value: undefined,
        probability: undefined,
        status: "pending",
        sent_date: undefined,
        response_deadline: undefined,
        expected_invoicing_date: undefined,
        tags: [],
        description: "",
        tools: [],
        led_by: undefined,
        initiative_id: undefined,
      });
    }
  }, [proposal?.id]); // Fixed: removed 'form' dependency to prevent infinite loops

  // Update existing attachments when proposalDocuments changes
  useEffect(() => {
    if (proposalDocuments) {
      setExistingAttachments(proposalDocuments);
    }
  }, [proposalDocuments]);

  // Create/Update proposal mutation
  const saveProposalMutation = useMutation({
    mutationFn: async (data: ProposalFormData) => {
      const proposalData = {
        title: data.title,
        client_id: data.client_id,
        value: data.value,
        final_value: data.final_value || null,
        probability: data.probability ?? 100,
        status: data.status,
        sent_date: data.sent_date ? data.sent_date.toISOString().split('T')[0] : null,
        response_deadline: data.response_deadline ? data.response_deadline.toISOString().split('T')[0] : null,
        expected_invoicing_date: data.expected_invoicing_date ? 
          new Date(data.expected_invoicing_date.getTime() - data.expected_invoicing_date.getTimezoneOffset() * 60000)
            .toISOString().split('T')[0] : null,
        tags: data.tags || [],
        description: data.description || null,
        tools: data.tools || [],
        led_by: data.led_by || null,
        initiative_id: data.initiative_id || null,
        ...(proposal ? {} : { created_by: user?.id }),
      };

      if (proposal) {
        // Update existing proposal
        const { data: updatedProposal, error } = await supabase
          .from("proposals")
          .update(proposalData)
          .eq("id", proposal.id)
          .select()
          .single();

        if (error) throw error;
        return updatedProposal;
      } else {
        // Create new proposal
        const { data: newProposal, error } = await supabase
          .from("proposals")
          .insert(proposalData)
          .select()
          .single();

        if (error) throw error;
        return newProposal;
      }
    },
    onSuccess: async (saved: any) => {
      try {
        // If an attachment is selected, upload it and create a proposal_documents row
        if (attachment && saved?.id) {
          if (attachment.size > MAX_FILE_SIZE) {
            toast({
              title: "File too large",
              description: "Maximum size is 10MB.",
              variant: "destructive",
            });
          } else {
            const safeName = attachment.name.replace(/[^a-zA-Z0-9._-]/g, "_");
            const filePath = `${saved.id}/${Date.now()}_${safeName}`;

            const { error: uploadError } = await supabase.storage
              .from("proposal-documents")
              .upload(filePath, attachment, {
                upsert: true,
                contentType: attachment.type || undefined,
              });
            if (uploadError) throw uploadError;

            const { error: docError } = await supabase.from("proposal_documents").insert({
              proposal_id: saved.id,
              file_name: attachment.name,
              file_path: filePath,
              mime_type: attachment.type || null,
              file_size: attachment.size,
              uploaded_by: user?.id ?? null,
            });
            if (docError) throw docError;

            toast({ title: "File uploaded", description: "The attachment was uploaded successfully." });
          }
        }
      } catch (e: any) {
        toast({
          title: "Attachment upload failed",
          description: e?.message || "Could not upload the file",
          variant: "destructive",
        });
      } finally {
        setAttachment(null);
        queryClient.invalidateQueries({ queryKey: ["proposals"] });
        queryClient.invalidateQueries({ queryKey: ["proposal-statistics"] });
      }

      onOpenChange(false);
      form.reset();
      toast({
        title: "Success",
        description: proposal ? "Proposal updated successfully" : "Proposal created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || `Failed to ${proposal ? 'update' : 'create'} proposal`,
        variant: "destructive",
      });
    },
  });

  // Delete existing attachment
  const deleteExistingAttachment = async (documentId: string, filePath: string) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("proposal-documents")
        .remove([filePath]);
      
      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("proposal_documents")
        .delete()
        .eq("id", documentId);
      
      if (dbError) throw dbError;

      // Update local state
      setExistingAttachments(prev => prev.filter(doc => doc.id !== documentId));
      
      toast({
        title: "File deleted",
        description: "The attachment was deleted successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Delete failed",
        description: error.message || "Could not delete the file",
        variant: "destructive",
      });
    }
  };

  // Get file preview URL from storage
  const getFilePreviewUrl = (filePath: string) => {
    const { data } = supabase.storage
      .from("proposal-documents")
      .getPublicUrl(filePath);
    return data.publicUrl;
  };

  const onSubmit = (data: ProposalFormData) => {
    saveProposalMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{proposal ? "Edit Proposal" : "Create New Proposal"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Proposal Title</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Brand U&A για XYZ Group" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="client_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {clients?.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="led_by"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Led by</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="initiative">Initiative</SelectItem>
                        <SelectItem value="custom_activity">Custom activity</SelectItem>
                        <SelectItem value="customer_request">Customer request</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Conditional Initiative dropdown */}
            {form.watch("led_by") === "initiative" && (
              <FormField
                control={form.control}
                name="initiative_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Initiative</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select initiative" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {initiatives?.map(initiative => (
                          <SelectItem key={initiative.id} value={initiative.id}>
                            {initiative.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {statusOptions.map(status => (
                        <SelectItem key={status} value={status}>
                          {statusDisplayMap[status as keyof typeof statusDisplayMap]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minimum Value (€)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="0" 
                        placeholder="0" 
                        value={String(field.value || "")}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="final_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Value (€)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="0" 
                        placeholder="0" 
                        value={String(field.value || "")}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="probability"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Probability (%)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="0" 
                        max="100"
                        placeholder="e.g., 75"
                        value={String(field.value ?? "")}
                        onChange={(e) => {
                          const v = e.target.value;
                          field.onChange(v === "" ? undefined : Math.max(0, Math.min(100, Number(v))));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expected_invoicing_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Expected Invoicing</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, "PPP") : "Select date"}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          className="p-3"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tools"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tools</FormLabel>
                  <FormControl>
                    <MultiSelectProducts 
                      products={products.map(p => ({ 
                        id: p.id, 
                        name: p.name, 
                        base_price: null 
                      }))}
                      value={field.value || []}
                      onChange={field.onChange}
                      placeholder="Select tools"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="sent_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Sent Date (Optional)</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, "PPP") : "Not sent yet"}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          className="p-3"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="response_deadline"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Response Deadline</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, "PPP") : "Select date"}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          className="p-3"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Brief description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Existing Attachments */}
            {existingAttachments.length > 0 && (
              <FormItem>
                <FormLabel>Existing Attachments</FormLabel>
                <div className="space-y-3">
                  {existingAttachments.map((doc) => {
                    const fileUrl = getFilePreviewUrl(doc.file_path);
                    const isImage = doc.mime_type?.startsWith("image/");
                    const isPdf = doc.mime_type === "application/pdf";
                    
                    return (
                      <div key={doc.id} className="border rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{doc.file_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteExistingAttachment(doc.id, doc.file_path)}
                          >
                            Delete
                          </Button>
                        </div>
                        
                        {/* File Preview */}
                        {isImage && (
                          <img
                            src={fileUrl}
                            alt={`Preview of ${doc.file_name}`}
                            className="max-h-48 w-full object-contain rounded-md border"
                            loading="lazy"
                          />
                        )}
                        
                        {isPdf && (
                          <div className="space-y-2">
                            <object
                              data={fileUrl}
                              type="application/pdf"
                              width="100%"
                              height="240"
                              className="rounded-md border"
                            >
                              <div className="p-4 text-center border rounded-md bg-gray-50">
                                <p className="text-sm text-muted-foreground mb-2">
                                  Cannot preview PDF in browser.
                                </p>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(fileUrl, "_blank")}
                                >
                                  Open in new tab
                                </Button>
                              </div>
                            </object>
                          </div>
                        )}
                        
                        {!isImage && !isPdf && (
                          <div className="p-4 text-center border rounded-md bg-gray-50">
                            <p className="text-sm text-muted-foreground mb-2">
                              Preview not available for this file type.
                            </p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(fileUrl, "_blank")}
                            >
                              Download file
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </FormItem>
            )}

            {/* New Attachment (optional) */}
            <FormItem>
              <FormLabel>
                {existingAttachments.length > 0 ? "Add New Attachment" : "Attachment"} 
                (PDF or image, up to 10MB, optional)
              </FormLabel>
              <FormControl>
                <Input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => {
                    const f = e.currentTarget.files?.[0] || null;
                    if (f && f.size > MAX_FILE_SIZE) {
                      setAttachment(null);
                      e.currentTarget.value = "";
                      toast({
                        title: "File too large",
                        description: "Maximum size is 10MB.",
                        variant: "destructive",
                      });
                    } else {
                      setAttachment(f);
                    }
                  }}
                />
              </FormControl>
              {attachment && (
                <div className="text-xs text-muted-foreground space-y-2">
                  <p>
                    Selected: {attachment.name} ({(attachment.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                  {previewUrl && (
                    <>
                      {attachment.type?.startsWith("image/") ? (
                        <img
                          src={previewUrl}
                          alt={`Preview of ${attachment.name}`}
                          className="max-h-48 rounded-md border"
                          loading="lazy"
                        />
                      ) : attachment.type === "application/pdf" ? (
                        <object
                          data={previewUrl}
                          type="application/pdf"
                          width="100%"
                          height="240"
                          className="rounded-md border"
                        >
                          <p>
                            Cannot preview PDF in browser.{" "}
                            <a href={previewUrl} target="_blank" rel="noreferrer" className="underline">
                              Open in new tab
                            </a>
                          </p>
                        </object>
                      ) : null}
                    </>
                  )}
                </div>
              )}
            </FormItem>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={saveProposalMutation.isPending}
            >
              {saveProposalMutation.isPending 
                ? (proposal ? "Updating..." : "Creating...") 
                : (proposal ? "Update Proposal" : "Create Proposal")
              }
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}